'use client';

import { m, useReducedMotion } from 'motion/react';
import { useLocale } from 'next-intl';
import type { MouseEvent } from 'react';

import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { ArrowDown01IconSolid, ArrowUp01IconSolid } from '@/components/ui/icon/glyphs';
import { formatCountFor } from '@/lib/format/format-count';
import { MOTION_TAP } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

const REACTION_COUNT_ANIMATION_DURATION_MS = 300 as const;
const VOTE_ARROW_TAP = { scale: 0.97, transition: MOTION_TAP } as const;

export interface ReactionVoteButtonsProps {
  reaction: 'up' | 'down' | null;
  upCount: number;
  downCount: number;
  upAriaLabel: string;
  downAriaLabel: string;
  onReact: (next: 'up' | 'down', e: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  size?: 'md' | 'sm';
}

export function ReactionVoteButtons({
  reaction,
  upCount,
  downCount,
  upAriaLabel,
  downAriaLabel,
  onReact,
  disabled = false,
  className,
  size = 'md',
}: ReactionVoteButtonsProps): React.ReactNode {
  const prefersReducedMotion = useReducedMotion();
  const formatCount = formatCountFor(useLocale());
  const net = upCount - downCount;
  const isSmall = size === 'sm';
  const wrapperHeight = isSmall ? 'h-6' : 'h-8';
  const buttonCellSize = isSmall ? 'h-6 w-5 p-0' : 'h-8 w-6 p-0';
  const arrowSize = isSmall ? 'size-3.5' : 'size-4';

  return (
    <div
      data-size={size}
      className={cn(
        'inline-flex items-center gap-0 px-1 text-foreground-secondary',
        wrapperHeight,
        className,
      )}
    >
      <AnimatedButton
        variant="tertiary"
        size="sm"
        disableTapScale
        onClick={(e: MouseEvent<HTMLButtonElement>): void => onReact('up', e)}
        disabled={disabled}
        aria-label={upAriaLabel}
        aria-pressed={reaction === 'up'}
        className={buttonCellSize}
      >
        {prefersReducedMotion === true ? (
          <Icon
            icon={ArrowUp01IconSolid}
            className={cn(
              arrowSize,
              reaction === 'up' ? 'fill-rating-good text-rating-good' : 'text-foreground-secondary',
            )}
          />
        ) : (
          <m.span className="inline-flex origin-center" whileTap={VOTE_ARROW_TAP}>
            <Icon
              icon={ArrowUp01IconSolid}
              className={cn(
                arrowSize,
                reaction === 'up'
                  ? 'fill-rating-good text-rating-good'
                  : 'text-foreground-secondary',
              )}
            />
          </m.span>
        )}
      </AnimatedButton>
      <output className="-mx-0.5 min-w-6 shrink-0 text-center text-body font-medium tabular-nums text-foreground-primary">
        <AnimatedNumber
          value={net}
          format={formatCount}
          duration={REACTION_COUNT_ANIMATION_DURATION_MS}
        />
      </output>
      <AnimatedButton
        variant="tertiary"
        size="sm"
        disableTapScale
        onClick={(e: MouseEvent<HTMLButtonElement>): void => onReact('down', e)}
        disabled={disabled}
        aria-label={downAriaLabel}
        aria-pressed={reaction === 'down'}
        className={buttonCellSize}
      >
        {prefersReducedMotion === true ? (
          <Icon
            icon={ArrowDown01IconSolid}
            className={cn(
              arrowSize,
              reaction === 'down' ? 'fill-rating-bad text-rating-bad' : 'text-foreground-secondary',
            )}
          />
        ) : (
          <m.span className="inline-flex origin-center" whileTap={VOTE_ARROW_TAP}>
            <Icon
              icon={ArrowDown01IconSolid}
              className={cn(
                arrowSize,
                reaction === 'down'
                  ? 'fill-rating-bad text-rating-bad'
                  : 'text-foreground-secondary',
              )}
            />
          </m.span>
        )}
      </AnimatedButton>
    </div>
  );
}
